import { TItemDescriptions } from 'factions/factionTypes'
import {
  CHARGE_PHASE,
  COMBAT_PHASE,
  DURING_GAME,
  DURING_SETUP,
  END_OF_TURN,
  HERO_PHASE,
  MOVEMENT_PHASE,
  SHOOTING_PHASE,
  START_OF_ROUND,
  WOUND_ALLOCATION_PHASE,
} from 'types/phases'
import rule_sources from './rule_sources'
import meta_rule_sources from 'meta/rule_sources'

const Flavors = {
  'Border Guards': {
    effects: [
      {
        name: `Grim Sentinels - Passive`,
        desc: `Effect: Melee weapons used by friendly Ossiarch Bonereapers units have Anti-charge (+1 Rend) while they are wholly within neutral territory or wholly within 6" of both enemy and friendly territory.`,
        when: [COMBAT_PHASE],
      },
    ],
  },
  'Remorseless Conquerors': {
    effects: [
      {
        name: `Unfaltering Pace - Reaction: You declared a Run ability for a friendly Ossiarch Bonereapers unit`,
        desc: `Used by: The unit using that Run ability. 
        Effect: If you roll a 1-3 when determining the distance that unit can move, you can use a value of 4 instead.`,
        when: [MOVEMENT_PHASE],
      },
    ],
  },
  'Ruthless Legion': {
    effects: [
      {
        name: `Nadirite Wedge - Once Per Turn`,
        desc: `Declare: Pick a friendly Ossiarch Bonereapers unit that charged this turn to use this ability.
        Effect: Roll a dice. On a 3+, this unit's melee weapons, including Companion weapons, have Crit (2 Hits) for the rest of the turn.`,
        when: [COMBAT_PHASE],
      },
    ],
  },
  'The Inevitable Empire': {
    effects: [
      {
        name: `Silent Empire - Once Per Turn`,
        desc: `Declare: Pick an enemy unit to be the target. 
        Effect: Roll a dice. On a 3+, until the start of your next turn, the target has a maximum control score of 1 while it is within neutral territory or your territory.`,
        when: [HERO_PHASE],
      },
    ],
  },
  'Tithe Guards': {
    effects: [
      {
        name: `Plentiful Bone - Once Per Turn`,
        desc: `Declare: Pick a friendly non-Unique Ossiarch Bonereapers Wizard to use this ability, then pick up to 3 visible friendly non-Fly Ossiarch Bonereapers units wholly within 12" of that Wizard to be the targets. 
        Effect: Roll a D3 for each target. On a 2+: 
        If the target is damaged, Heal (X) the target where X is an amount equal to the roll. 
        If the target is not damaged, return a number of slain models to it with a combined Health characteristic equal to or less than the roll.`,
        when: [HERO_PHASE],
      },
    ],
  },
  'Hekatos Drillmasters': {
    effects: [
      {
        name: `Disciplined Hekatoi - Once Per Turn`,
        desc: `Declare: Pick a friendly non-Hero non-Fly Ossiarch Bonereapers Infantry unit to be the target. 
        Effect: Pick 1 of the following effects to apply until the start of your next turn: 
        The target has Ward (5+). 
        Add 1 to charge rolls for the target. 
        Add 1 to wound rolls for the targets attacks. 
        The targets melee weapons have Anti-charge (+1 Rend).`,
        when: [HERO_PHASE],
      },
    ],
  },
  'The Lance of Ossia (AoR)': {
    effects: [
      {
        name: `Praetorian Elite - Once Per Battle Round`,
        desc: `Declare: You must use this ability if there are any friendly Lance of Ossia Heroes on the battlefield. Pick a friendly Lance of Ossia Hero to be the target.
        Effect: You gain a number of relentless discipline points equal to the number after the target's Relentless Discipline keyword. At the end of the battle round, any unspent relentless discipline points are lost.`,
        when: [START_OF_ROUND],
      },
      {
        name: `Kavalos Lance - Passive`,
        desc: `Effect: When a friendly Lance of Ossia unit uses the Power Through command, the target does not have to have a lower Health characteristic than that friendly unit.`,
        when: [END_OF_TURN],
      },
      {
        name: `Vengeful Outriders`,
        desc: `Declare: Pick a friendly Lance of Ossia unit that has not used a Relentless Discipline ability this phase to be the target.
        Effect: Spend 1 relentless discipline point. If the target is reinforced and has more than half of its starting models, spend 2 relentless discipline points instead. Add 2" to the target's Move characteristic for the rest of the phase.`,
        when: [MOVEMENT_PHASE],
      },
      {
        name: `Silent Menace`,
        desc: `Declare: Pick a friendly Lance of Ossia unit that has not used a Relentless Discipline ability this phase to use this ability, then pick a visible enemy unit within 12" of it to be the target.
        Effect: Spend 1 relentless discipline point. If the target is reinforced and has more than half of its starting models, spend 2 relentless discipline points instead. The target cannot use the Redeploy command for the rest of the turn.`,
        when: [MOVEMENT_PHASE],
      },
      {
        name: `Fury of Zandtos`,
        desc: `Declare: Pick a friendly Lance of Ossia unit that charged this turn and has not used a Relentless Discipline ability this phase to be the target.
        Effect: Spend 1 relentless discipline point. If the target is reinforced and has more than half of its starting models, spend 2 relentless discipline points instead. The target's melee weapons, including Companion weapons, have Crit (2 Hits) for the rest of the turn.`,
        when: [CHARGE_PHASE],
      },
      {
        name: `Blood-Smeared Hooves`,
        desc: `Declare: Pick a friendly Lance of Ossia unit that charged this turn and has not used a Relentless Discipline ability this phase to be the target.
        Effect: Spend 1 relentless discipline point. If the target is reinforced and has more than half of its starting models, spend 2 relentless discipline points instead. Add 1 to the Rend characteristic of the target's weapons, including Companion weapons, for the rest of the turn.`,
        when: [COMBAT_PHASE],
      },
      ],
    },
    'The Null Myriad (AoR)': {
    effects: [
      {
        name: `Arcane Immunity - Passive`,
        desc: `Effect: Friendly Null Myriad units have Ward (4+) against damage points inflicted by Spell abilities, Prayer abilities, and abilities used by Manifestations.`,
        when: [DURING_GAME],
      },
      {
        name: `First of the Mortarchs - Passive`,
        desc: `Effect: Each time you make a casting roll for a friendly Arkhan the Black, if all the following are true, that spell cannot be unbound:
        The unmodified casting roll contains 2 or more results of the same value.
        The casting roll equals or exceeds the spell's casting value.
        The spell is not miscast.`,
        when: [HERO_PHASE],
      },
      {
        name: `Supernatural Absorption - Once Per Turn`,
        desc: `Declare: Pick an enemy Manifestation within a friendly Null Myriad unit's combat range to be the target.
        Effect: Roll a dice. On a 3+:
        The target is banished and removed from play.
        If that friendly unit is damaged, Heal (X) that friendly unit, where X is the target's Health characteristic.
        IF that friendly unit is not damaged, return a number of slain models to it with a combined Health characteristic of up to X, where X is the target's Health characteristic.`,
        when: [HERO_PHASE],
      },
      ],
    },
  /* 'Null Myriad': {
    effects: [
      {
        name: `Eldritch Nulls`,
        desc: `You can roll a dice each time a friendly Null Myriad unit is affected by a spell cast by an enemy unit or the abilities of an endless spell summoned by an enemy unit. On a 4+, ignore the effect of that spell or the effects of that endless spell's abilities on that unit.`,
        when: [HERO_PHASE],
        rule_sources: [
          rule_sources.BATTLETOME_OSSIARCH_BONEREAPERS,
          meta_rule_sources.BATTLESCROLL_NULLSTONE_CACHE,
        ],
      },
    ],
  },
  Crematorians: {
    effects: [
      {
        name: `Immolation`,
        desc: `Each time a friendly CREMATORIANS model is slain, before removing that model from play, you can pick 1 enemy unit within 3" of it and roll a number of dice equal to the Wounds characteristic of that model. For each 5+, that enemy unit suffers 1 mortal wound.`,
        when: [WOUND_ALLOCATION_PHASE],
      },
    ],
  }, */
} satisfies TItemDescriptions

// Note: We do NOT use tagAs for Flavors
export default Flavors
